import { AfterContentInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { FormService } from '../../../common/services/form.service';
import { MenyProduct } from '../../../common/models/meny-product.model';
import { FormCommonDataService } from '../../../common/services/form-common-data.service';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { debounceTime, finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Field } from 'src/sb-tools/typescript/models/field.model';
import { FormType } from '@sb-tools/typescript/enums/form-type.enum';
import { ClientField } from '../../../common/models/client-field.model';
import { TruncatePipe } from '@sb-tools/typescript/pipes/truncate.pipe';
import { FocusDirective } from '@sb-tools/typescript/directives/focus.directive';
import { SelectAccessibilityDirective } from '@form-field-module/common/directives/select-accessibility.directive';

@Component({
    selector: 'sb-meny-product',
    templateUrl: './meny-product.component.html',
    styleUrls: ['./meny-product.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    imports: [NgSelectModule, TruncatePipe, FormsModule, FocusDirective, SelectAccessibilityDirective, TranslateModule],
})
export class MenyProductComponent implements OnInit, AfterContentInit, OnDestroy {
    @Input() formField: Field | ClientField;
    @Input() formType: FormType;

    @ViewChild('select') selectElement: NgSelectComponent;

    isInitValue = true;
    isProductMetaDataHidden: boolean;
    labelText: string;
    loadingProducts: boolean;
    notFoundText: string;
    placeholderName: string;
    products: MenyProduct[];
    searchProductText: string;
    selectedValue;

    private searchProductsQueryUpdate = new Subject<any>();
    private unsubscriber = new Subject<void>();

    constructor(
        private formService: FormService,
        private formCommonDataService: FormCommonDataService,
        private translateService: TranslateService,
        private changeDetectorRef: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.labelText = this.formField.label || this.formField.name;
        this.selectedValue = this.formField.value || null;
        this.isProductMetaDataHidden = this.formType <= FormType.widgetAuthentication;
        this.placeholderName = this.formField.placeholder || this.translateService.instant('Product name');

        this.searchProductsQueryUpdate
            .pipe(debounceTime(300))
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((searchText) => {
                this.loadProducts(searchText);
                this.setNotFoundText();
            });
    }

    ngAfterContentInit() {
        // fix for error: Expression has changed after it was checked. Previous value for 'focusableSelectElement': 'undefined'. Current value: '[object Object]'
        this.changeDetectorRef.detectChanges();
    }

    cleanSelectedValue() {
        this.isInitValue = false;
        this.selectedValue = null;
        this.formField.value = null;
        this.formCommonDataService.updateMenyProductModel(null);

        setTimeout(() => {
            if (this.selectElement) {
                this.selectElement.focus();
            }
        });
    }

    customSearchFn(term: string, item: any) {
        return item;
    }

    onBlur() {
        if (!this.selectedValue) {
            this.selectElement.searchTerm = this.searchProductText;
        } else {
            this.searchProductText = this.selectElement.searchTerm = '';
            this.clearProductsList();
        }
    }

    onClear() {
        this.searchProductText = '';
        this.clearProductsList();
        this.formCommonDataService.updateMenyProductModel(null);
    }

    onSearch(value) {
        this.searchProductText = value.term;

        if (this.searchProductText && this.searchProductText.trim().length > 0) {
            this.searchProductsQueryUpdate.next(this.searchProductText.trim());
        } else {
            this.clearProductsList();
        }
        this.setNotFoundText();
    }

    onSelect() {
        this.formField.value = this.selectedValue ? this.selectedValue.name : null;
        this.formCommonDataService.updateMenyProductModel(this.selectedValue);
        this.isInitValue = false;
    }

    private clearProductsList() {
        this.products = [];
    }

    private loadProducts(searchText) {
        this.loadingProducts = true;
        this.formService
            .getMenyProducts(searchText)
            .pipe(
                finalize(() => {
                    this.loadingProducts = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((data) => {
                this.products = data;
            });
    }

    private setNotFoundText() {
        if (this.searchProductText?.length) {
            this.notFoundText = this.translateService.instant('No products found');
            return;
        }

        this.notFoundText = this.translateService.instant('start search product by name or ean code');
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
